import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import '../styles/page-layout.css';

const NetworkErrorPage: React.FC = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleRetry = () => {
    if (isOnline) {
      window.location.reload();
    }
  };

  return (
    <Layout
      title="Verbindungsfehler | Stimm.DEV"
      description="Es besteht ein Problem mit der Internetverbindung. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut."
      keywords="Netzwerk-Fehler, Offline, Verbindungsproblem"
    >
      <section className="wrapper">
        <div className="page-container" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              fontSize: '4rem',
              marginBottom: '1rem'
            }}>
              {isOnline ? '📡' : '📵'}
            </div>
            
            <h1 style={{ 
              fontSize: '3rem', 
              fontWeight: '800', 
              background: isOnline 
                ? 'linear-gradient(135deg, var(--primary-color), var(--secondary-color))'
                : 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '1rem'
            }}>
              {isOnline ? 'Verbindung wiederhergestellt' : 'Verbindungsfehler'}
            </h1>
            
            <p style={{ 
              fontSize: '1.1rem', 
              color: 'var(--text-muted)',
              marginBottom: '3rem',
              lineHeight: '1.6'
            }}>
              {isOnline 
                ? 'Ihre Internetverbindung ist wieder verfügbar. Sie können die Seite nun neu laden.'
                : 'Es scheint, als hätten Sie keine Internetverbindung. Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.'
              }
            </p>

            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginBottom: '3rem'
            }}>
              <button 
                onClick={handleRetry}
                disabled={!isOnline}
                style={{ 
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: isOnline ? 'var(--primary-color)' : 'transparent',
                  color: isOnline ? 'white' : 'var(--text-muted)',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  border: isOnline ? '2px solid var(--primary-color)' : '2px solid var(--border-color)',
                  boxShadow: isOnline ? '0 4px 12px rgba(0,0,0,0.1)' : '0 2px 6px rgba(0,0,0,0.05)',
                  cursor: isOnline ? 'pointer' : 'not-allowed',
                  opacity: isOnline ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (isOnline) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                    e.currentTarget.style.backgroundColor = 'var(--primary-color-dark)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isOnline) {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                    e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                  }
                }}
              >
                <i className="fas fa-sync-alt" style={{ marginRight: '0.5rem' }}></i>
                {isOnline ? 'Seite neu laden' : 'Warten auf Verbindung...'}
              </button>
              
              <Link 
                to="/" 
                style={{ 
                  textDecoration: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '1rem 2rem',
                  backgroundColor: 'transparent',
                  color: 'var(--primary-color)',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  border: '2px solid var(--primary-color)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.1)';
                  e.currentTarget.style.backgroundColor = 'var(--primary-color)';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--primary-color)';
                }}
              >
                <i className="fas fa-home" style={{ marginRight: '0.5rem' }}></i>
                Zur Startseite
              </Link>
            </div>

            <div style={{ 
              padding: '2rem',
              background: 'var(--card-background)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)',
              textAlign: 'left'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '1rem'
              }}>
                <div style={{
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  backgroundColor: isOnline ? '#27ae60' : '#e74c3c',
                  marginRight: '0.5rem'
                }}></div>
                <h3 style={{ margin: 0, color: 'var(--text-color)' }}>
                  Verbindungsstatus: {isOnline ? 'Online' : 'Offline'}
                </h3>
              </div>
              
              <h4 style={{ marginBottom: '1rem', color: 'var(--text-color)' }}>
                Lösungsschritte:
              </h4>
              
              <ol style={{ 
                color: 'var(--text-muted)',
                lineHeight: '1.8',
                paddingLeft: '1.5rem',
                marginBottom: '2rem'
              }}>
                <li>Prüfen Sie Ihre WLAN- oder Mobilverbindung</li>
                <li>Starten Sie Ihren Router neu (falls WLAN verwendet wird)</li>
                <li>Deaktivieren Sie VPN-Verbindungen temporär</li>
                <li>Prüfen Sie die Firewall-Einstellungen</li>
                <li>Versuchen Sie es mit einem anderen Browser</li>
              </ol>

              {!isOnline && (
                <div style={{ 
                  padding: '1rem',
                  background: '#fff3cd',
                  borderRadius: '0.5rem',
                  borderLeft: '4px solid #ffc107',
                  marginBottom: '1rem'
                }}>
                  <strong style={{ color: '#856404' }}>
                    Offline-Modus erkannt
                  </strong>
                  <p style={{ margin: '0.5rem 0 0 0', color: '#856404' }}>
                    Einige Funktionen sind möglicherweise nicht verfügbar. 
                    Die Seite wird automatisch aktualisiert, sobald die Verbindung wiederhergestellt ist.
                  </p>
                </div>
              )}

              <div style={{ 
                padding: '1rem',
                background: 'var(--primary-color-light)',
                borderRadius: '0.5rem',
                borderLeft: '4px solid var(--primary-color)'
              }}>
                <strong style={{ color: 'var(--text-color)' }}>
                  Problem weiterhin vorhanden?
                </strong>
                <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-muted)' }}>
                  Kontaktieren Sie unseren Support unter{' '}
                  <a 
                    href="mailto:support@stimm.dev?subject=Verbindungsproblem" 
                    style={{ color: 'var(--primary-color)', textDecoration: 'none' }}
                  >
                    support@stimm.dev
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NetworkErrorPage;
